import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/stripe-status/${sessionId}`);
        if (data.payment_status === "paid" && data.access_token) {
          if (cancelled) return;
          setState("paid");
          setTimeout(() => navigate(`/access/${data.access_token}`), 1500);
          return;
        }
      } catch {}
      tries += 1;
      if (tries < 15 && !cancelled) setTimeout(poll, 2000);
      else if (!cancelled) setState("timeout");
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6" data-testid="payment-success-page">
      {state === "checking" && (
        <>
          <Loader2 size={32} className="animate-spin text-brand" />
          <p className="font-display text-2xl font-bold tracking-tight" data-testid="payment-checking-label">Confirming your payment...</p>
          <p className="text-sm text-zinc-500">This takes a few seconds — don't close this page.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 size={40} className="text-emerald-500" />
          <p className="font-display text-3xl font-black tracking-tight" data-testid="payment-confirmed-label">Payment confirmed!</p>
          <p className="text-sm text-zinc-400">Opening your course...</p>
        </>
      )}
      {(state === "timeout" || state === "error") && (
        <>
          <AlertCircle size={36} className="text-amber-500" />
          <p className="font-display text-2xl font-bold tracking-tight" data-testid="payment-pending-label">
            {state === "timeout" ? "Payment is still processing." : "We couldn't find that payment."}
          </p>
          <p className="max-w-md text-center text-sm leading-relaxed text-zinc-500">
            If the money was deducted, your access link will activate automatically — message us on WhatsApp and we'll confirm it right away.
          </p>
          <Link to="/" className="link-underline text-sm uppercase tracking-widest text-brand">Back to home</Link>
        </>
      )}
    </div>
  );
}
